import { Link } from "react-router";
import type { Attendance, OccurrenceKey, PairOccurrence } from "@edu/shared";
import { ATTENDANCE_TITLES, paymentWindowStart } from "@edu/shared";
import { absText, dayText, timeText } from "../../lib/when.ts";
import { assignHref } from "../schedule/entry-homework.ts";
import { nextAttendance } from "./StudentMarks.tsx";

/**
 * Занятие строкой в карточке ученика: дата, занятие, «Был», «Оплата», действие.
 *
 * С 26.09.2026 строка это ЗАНЯТИЕ, а не урок (решение владельца): урок
 * приложен к занятию, если оно им стало, и от него зависят только название,
 * ссылка «задать домашнюю» и действие. Колонок «Был» и «Оплата» нет вовсе, когда
 * их учёт выключен: решает список, строка только следует его набору колонок.
 *
 * Слоты в одном порядке у всех строк и не зависят от того, что у занятия
 * заполнено: раньше дата стояла отдельным абзацем под названием, и глазу не за
 * что было зацепиться при быстром чтении сверху вниз.
 *
 * ⚠️ **Колонки «Состояние» больше нет.** Завершён урок или создан, видно по
 * действию («Архив», «открывать нечего», «Войти»), и отдельное слово повторяло
 * бы его. Осталось одно состояние, которое требует действия прямо сейчас:
 * идущий урок, и он помечен пилюлей под датой.
 */
export function StudentOccurrenceRow(props: {
  occurrence: PairOccurrence;
  studentUserId: string;
  /** Снят один раз на весь список: строке своё «сейчас» не нужно. */
  now: number;
  /** Колонка «Был»: учёт посещений включён. */
  attendance: boolean;
  /** Колонка «Оплата»: учёт оплат включён, и пара платит поурочно. */
  payment: boolean;
  /** Будущее: чипов учёта у него нет, отмечать ещё нечего. */
  upcoming: boolean;
  busy: boolean;
  onMark: (key: OccurrenceKey, patch: { paid?: boolean; attendance?: Attendance | null }) => void;
}) {
  const o = props.occurrence;
  const lesson = o.lesson;
  const live = lesson?.status === "live";
  /*
    Чипы учёта только у прошедшего занятия из учёта. Осиротевший урок (занятия
    которого расписание больше не даёт) счётчик не видит, и кнопка «отметить»
    у него врала бы про долг: нажатие ничего бы не изменило в числе у заголовка.
  */
  const markable = !props.upcoming && !o.unaccounted;
  /*
    Чип оплаты вдобавок только внутри окна учёта: занятие старше него не видит
    ни счётчик «не отмечено», ни фильтр, и «отметить» у него ничего бы не
    изменило. Граница та же функция, что у сервера. Посещаемость от окна не
    зависит: «был на занятии» это факт, а не деньги.
  */
  const payable = markable && o.at >= paymentWindowStart(props.now);
  const paid = o.paidAt !== null;

  return (
    <li className="row" data-dim={props.payment && payable && paid}>
      <span className="cell row__when" data-label="Дата" title={absText(o.at)}>
        {dayText(o.at, props.now)}
        {/* Цвет только у идущего: оно требует действия сейчас, а прошедшее и
            будущее это состояния покоя, и красить их значит гасить первое */}
        <span className="row__sub">
          {live ? (
            <span className="pill" data-tone="ok">
              идёт
            </span>
          ) : (
            timeText(o.at)
          )}
        </span>
      </span>

      <span className="cell row__name">
        {/* Занятия без урока законны и часты: урок в Zoom нашей строки не
            заводит. Пустое место здесь читалось бы как поломка, поэтому оно
            занято словами */}
        {lesson?.title ?? <span className="row__void">урока в приложении не было</span>}
        {/*
          Вход в форму домашки с занятия (3.2 проекта домашки): та же форма с
          предзаполненными учеником, заготовкой и подписью «задана после
          занятия». Занятие, чью заготовку уже стёрла уборка, домашку из себя
          задать не может, и ссылки у него нет: честная цена слабой связи.
          Компиляция всё равно идёт из ИСТОЧНИКОВ заготовки, а не из элементов
          урока: у урока ответы могли быть раскрыты.

          Тихой второй строкой, а не кнопкой в ряду с главной: это редкий путь,
          и рядом с «Войти» он читался бы равным ей по весу.

          ⚠️ Адрес собирается ОБЩЕЙ функцией, а не строкой здесь: вторая дверь
          в ту же форму живёт в карточке занятия в расписании, и разъехавшиеся
          имена параметров дали бы дверь, которая молча не подставляет ни
          ученика, ни заготовку.
        */}
        {lesson !== null && lesson.planId !== null && (
          <span className="row__sub">
            <Link
              className="textlink"
              to={assignHref({
                studentUserId: props.studentUserId,
                planId: lesson.planId,
                lessonId: lesson.id,
              })}
            >
              задать домашку
            </Link>
          </span>
        )}
      </span>

      {/*
        ⚠️ Пустая ячейка рисуется БЕЗ `data-label` и без содержимого вовсе: на
        узкой раскладке слот подписывает себя сам, и подпись «Был:» над
        пустотой читалась бы как недогруженное значение. `:empty` там же
        прячет её целиком (`styles.css`, `.list--stu-occ`).
      */}
      {props.attendance &&
        (markable ? (
          <span className="cell" data-label="Был">
            {/*
              Посещение это три состояния, и переключается оно по кругу: не
              отмечено, был, не пришёл. Три кнопки в строку не влезают, а
              выпадающий список ради трёх значений это два щелчка вместо одного.
            */}
            <button
              type="button"
              className="chip"
              data-tone={
                o.attendance === "absent" ? "warn" : o.attendance === "present" ? "ok" : undefined
              }
              disabled={props.busy}
              title="Не отмечено, был, не пришёл"
              onClick={() => props.onMark(o.key, { attendance: nextAttendance(o.attendance) })}
            >
              {o.attendance === null ? "—" : ATTENDANCE_TITLES[o.attendance]}
            </button>
          </span>
        ) : (
          <span className="cell" />
        ))}

      {/*
        ⚠️ Цвета у долга нет. Ни красного, ни оранжевого: «не отмечено» это не
        нарушение, преподаватель мог просто знать об оплате и не отмечать.
        Отмеченное, наоборот, гасится, чтобы взгляд цеплялся за неотмеченное
        отсутствием отметки, а не тревогой.
      */}
      {props.payment &&
        (payable ? (
          <span className="cell" data-label="Оплата">
            <button
              type="button"
              className="chip"
              data-tone={paid ? "ok" : undefined}
              disabled={props.busy}
              title={paid ? `Отмечено ${absText(o.paidAt ?? 0)}` : "Отметить оплаченным"}
              onClick={() => props.onMark(o.key, { paid: !paid })}
            >
              {paid ? "оплачено" : "отметить"}
            </button>
          </span>
        ) : (
          <span className="cell" />
        ))}

      <span className="cell row__act">{lesson !== null && <LessonAct lesson={lesson} />}</span>
    </li>
  );
}

/**
 * Одно действие на строку, и оно ведёт в АРХИВ, когда снимок доски уже есть, и
 * в комнату, когда его нет. Разница не косметическая: комната завершённого
 * урока поднимает Yjs и LiveKit ради того, что уже лежит снимком, а архив
 * открывается мгновенно и без медиасервера.
 *
 * Занятие без урока действия не имеет, и ячейка пуста: войти туда можно из
 * расписания, а у прошедшего занятия в Zoom открывать у нас нечего.
 */
function LessonAct({ lesson }: { lesson: NonNullable<PairOccurrence["lesson"]> }) {
  if (lesson.hasBoardSnapshot) {
    return (
      <Link className="btn btn--sm" to={`/archive/${lesson.id}`}>
        Архив
      </Link>
    );
  }
  if (lesson.status === "ended") {
    /*
      Завершённое занятие, на доске которого ничего не сохранилось. Открывать
      нечего: архива у него не будет никогда, а «Войти» в завершённый урок мы не
      предлагаем нигде. Слот при этом занят словами, а не пустотой, иначе строка
      снова поедет.
    */
    return <span className="row__void">открывать нечего</span>;
  }
  return (
    <Link className="btn btn--sm" to={`/lesson/${lesson.id}`}>
      Войти
    </Link>
  );
}
